import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

import AuthRoleSwitch from "../../components/AuthRoleSwitch/AuthRoleSwitch";
import { FormModal } from "../../components/Modals";
import { useAuth } from "../../context/AuthContext";
import {
  getAuthTokens,
  type RegistrationLegalDocument,
} from "../../services/authService/authService";
import type { AuthRole } from "../../types/auth";
import { getServerErrorData } from "../../utils/httpError";
import { logger } from "../../utils/logger";
import { useNotify } from "../../context/NotificationContext";
import {
  ConsentCheckbox,
  ConsentCopy,
  ConsentIntro,
  ConsentLink,
  ConsentList,
  ConsentRow,
  ConsentSection,
  ConsentStatus,
} from "../RegistrationPage/RegistrationPage.styles";
import { Container, Logo } from "./LoginPage.styles";
import { LoginFormProps } from "./LoginPage.types";

const formatLegalDocumentVersion = (version: string) =>
  version.replace(/\b(\d{4})-(\d{2})-(\d{2})\b/g, "$3.$2.$1");

const LoginPage: React.FC<LoginFormProps> = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialRole = searchParams.get("role") === "student" ? "student" : "teacher";
  const next = searchParams.get("next") || "";
  const { login } = useAuth();
  const toast = useNotify();
  const [role, setRole] = useState<AuthRole>(initialRole);
  const [externalErrors, setExternalErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [pendingCredentials, setPendingCredentials] = useState<{ email: string; password: string } | null>(null);
  const [consentDocuments, setConsentDocuments] = useState<RegistrationLegalDocument[]>([]);
  const [acceptedDocumentIds, setAcceptedDocumentIds] = useState<number[]>([]);
  const [consentError, setConsentError] = useState("");
  const memoizedExternalErrors = useMemo(() => externalErrors, [externalErrors]);

  const loginFields = useMemo(
    () => [
      {
        name: "email",
        type: "email",
        placeholder: t("auth.email"),
        required: true,
        width: "369px",
        validate: (value: string) =>
          /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) ? null : t("auth.emailError"),
      },
      {
        name: "password",
        type: "password",
        width: "369px",
        placeholder: t("auth.password"),
        required: true,
        validate: (value: string) =>
          value.trim().length > 0 ? null : t("auth.passwordError"),
      },
    ],
    [t],
  );

  const consentRequired = Boolean(pendingCredentials && consentDocuments.length > 0);
  const fields = consentRequired ? [] : loginFields;
  const allDocumentsAccepted = consentDocuments.length > 0
    && consentDocuments.every((document) => acceptedDocumentIds.includes(document.id));

  const nextSuffix = next ? `&next=${encodeURIComponent(next)}` : "";
  const footerLinks = consentRequired
    ? [{ text: "Вернуться ко входу", href: `/login?role=${role}${nextSuffix}`, variant: "secondary" as const }]
    : [
        { text: t("auth.createAccount"), href: `/signup?role=${role}${nextSuffix}`, variant: "primary" as const },
        { text: t("auth.passwordSos"), href: `/reset-password?role=${role}`, variant: "secondary" as const },
      ];

  const finishLogin = () => {
    login(role);
    const roleHome = role === "student" ? "/student/courses" : "/courses";
    const safeNext = role === "student" && next.startsWith("/") && !next.startsWith("//") ? next : roleHome;
    navigate(safeNext, { replace: true });
  };

  const toggleDocument = (documentId: number) => {
    setAcceptedDocumentIds((current) =>
      current.includes(documentId)
        ? current.filter((id) => id !== documentId)
        : [...current, documentId],
    );
    setConsentError("");
  };

  const handleSubmit = async (formData: Record<string, string>) => {
    setIsLoading(true);
    setExternalErrors({});
    setConsentError("");
    try {
      const credentials = pendingCredentials || {
        email: formData.email,
        password: formData.password,
      };
      const acceptedIds = consentRequired ? acceptedDocumentIds : undefined;
      const result = await getAuthTokens(
        credentials.email,
        credentials.password,
        role,
        acceptedIds,
      );

      if (result.consent_required) {
        setPendingCredentials(credentials);
        setConsentDocuments(result.documents);
        setAcceptedDocumentIds([]);
        return;
      }

      finishLogin();
    } catch (error) {
      const serverErrors = getServerErrorData(error);
      if (serverErrors) {
        const formattedErrors: Record<string, string> = {};
        Object.entries(serverErrors).forEach(([key, value]) => {
          if (key === "accepted_document_ids" || key === "detail") {
            setConsentError(String(value));
          } else {
            formattedErrors[key] = String(value);
          }
        });
        setExternalErrors(formattedErrors);
      } else {
        logger.error("Ошибка авторизации:", error);
        toast.show({ severity: "error", summary: "Ошибка", detail: "Ошибка авторизации", life: 3000 });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const consentContent = consentRequired ? (
    <ConsentSection aria-label="Документы для подтверждения">
      <ConsentIntro>
        Чтобы продолжить работу, ознакомьтесь и подтвердите документы, которые вы ещё не принимали.
      </ConsentIntro>
      {consentError && <ConsentStatus $error>{consentError}</ConsentStatus>}
      <ConsentList>
        {consentDocuments.map((document) => (
          <ConsentRow key={document.id}>
            <ConsentCheckbox
              type="checkbox"
              checked={acceptedDocumentIds.includes(document.id)}
              onChange={() => toggleDocument(document.id)}
              aria-label={`Принять документ ${document.title}`}
            />
            <ConsentCopy>
              Я ознакомился(ась) и принимаю{" "}
              <ConsentLink
                href={document.url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(event) => event.stopPropagation()}
              >
                {document.title}
              </ConsentLink>
              {document.version && `, версия ${formatLegalDocumentVersion(document.version)}`}
            </ConsentCopy>
          </ConsentRow>
        ))}
      </ConsentList>
    </ConsentSection>
  ) : undefined;

  return (
    <Container>
      <Logo>LISRENT</Logo>
      <FormModal
        title={consentRequired ? "Подтвердите документы" : t("auth.in")}
        fields={fields}
        onSubmit={handleSubmit}
        hasBackdrop={false}
        footerLinks={footerLinks}
        submitButtonText={consentRequired ? "Подтвердить и войти" : t("auth.login")}
        externalErrors={memoizedExternalErrors}
        isLoading={isLoading}
        beforeSubmitContent={consentContent}
        submitDisabled={consentRequired && !allDocumentsAccepted}
        headerContent={consentRequired ? undefined : (
          <AuthRoleSwitch
            value={role}
            onChange={(nextRole) => {
              setRole(nextRole);
              setExternalErrors({});
            }}
            disabled={isLoading}
            label="Войти как"
          />
        )}
      />
    </Container>
  );
};

export default LoginPage;
